import ExcelJS from 'exceljs'
import JSZip from 'jszip'
import type { SheetDetail } from './types'

const MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const HEADERS = [
  'Pareja', 'Padre', 'Anilla padre', 'Plumaje padre', 'Madre', 'Anilla madre',
  'Plumaje madre', 'Nº anilla pichón', 'Plumaje pichón', 'Sexo', 'Observaciones'
]

export function groupSheetsByYear(sheets: SheetDetail[]) {
  const groups = new Map<number, SheetDetail[]>()
  sheets.forEach(sheet => {
    const items = groups.get(sheet.anio) || []
    items.push(sheet)
    groups.set(sheet.anio, items)
  })
  groups.forEach(items => items.sort((a, b) => a.hoja - b.hoja))
  return [...groups.entries()].sort(([a], [b]) => a - b)
}

function cloneStyle(source?: ExcelJS.Cell) {
  return source?.style ? structuredClone(source.style) : undefined
}

export function addYearWorksheet(workbook: ExcelJS.Workbook, year: number, records: SheetDetail[], template?: ExcelJS.Worksheet) {
  const previous = workbook.getWorksheet(String(year))
  if (previous) workbook.removeWorksheet(previous.id)
  const sheet = workbook.addWorksheet(String(year), { views: [{ state: 'frozen', ySplit: 1 }] })
  sheet.columns = HEADERS.map((_, index) => ({ width: [12, 22, 18, 18, 28, 18, 20, 20, 20, 14, 30][index] }))

  const header = sheet.getRow(1)
  header.values = HEADERS
  HEADERS.forEach((_, index) => {
    const style = cloneStyle(template?.getCell(1, index + 1))
    if (style) header.getCell(index + 1).style = style
    else {
      header.getCell(index + 1).font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } }
      header.getCell(index + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } }
      header.getCell(index + 1).alignment = { horizontal: 'center' }
    }
  })

  let rowNumber = 2
  records.forEach(record => {
    const pair = sheet.getRow(rowNumber++)
    pair.values = [`${record.hoja}/${record.anio}`, record.padre.nombre || '', record.padre.anilla || '', record.padre.plumaje || '', record.madre.nombre || '', record.madre.anilla || '', record.madre.plumaje || '', '', '', '', '']
    pair.eachCell({ includeEmpty: true }, (cell, column) => {
      const style = cloneStyle(template?.getCell(2, column))
      if (style) cell.style = style
      else {
        cell.font = { name: 'Calibri', size: 11, bold: true }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2F0D9' } }
      }
      cell.numFmt = '@'
    })
    record.pichones.forEach(chick => {
      const row = sheet.getRow(rowNumber++)
      row.values = ['', '', '', '', '', '', '', chick.anilla || '', chick.plumaje || '', chick.sexo || '', chick.observaciones || '']
      row.eachCell({ includeEmpty: true }, (cell, column) => {
        const style = cloneStyle(template?.getCell(3, column))
        if (style) cell.style = style
        cell.numFmt = '@'
      })
    })
    rowNumber += 1
  })
  sheet.autoFilter = { from: 'A1', to: 'K1' }
  return sheet
}

function xmlEscape(value: unknown) {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')
}

function columnName(index: number) {
  let value = ''
  for (let number = index; number > 0; number = Math.floor((number - 1) / 26)) value = String.fromCharCode(65 + ((number - 1) % 26)) + value
  return value
}

function styleIdsForRow(xml: string, row: number) {
  const match = xml.match(new RegExp(`<(?:\\w+:)?row[^>]*\\br="${row}"[^>]*>([\\s\\S]*?)<\\/(?:\\w+:)?row>`))
  const styles = Array(HEADERS.length).fill('')
  if (!match) return styles
  for (const cell of match[1].matchAll(/<(?:\w+:)?c\s+([^>]*?)>/g)) {
    const ref = cell[1].match(/\br="([A-Z]+)\d+"/)?.[1]
    const style = cell[1].match(/\bs="(\d+)"/)?.[1]
    if (!ref || !style) continue
    const index = ref.split('').reduce((total, letter) => total * 26 + letter.charCodeAt(0) - 64, 0)
    if (index <= HEADERS.length) styles[index - 1] = style
  }
  return styles
}

function inlineCell(ref: string, value: unknown, style = '') {
  const styleAttribute = style ? ` s="${style}"` : ''
  return `<c r="${ref}" t="inlineStr"${styleAttribute}><is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`
}

function buildSheetXml(year: number, records: SheetDetail[], templateXml = '') {
  const headerStyles = styleIdsForRow(templateXml, 1)
  const pairStyles = styleIdsForRow(templateXml, 2)
  const chickStyles = styleIdsForRow(templateXml, 3)
  const templateCols = templateXml.match(/<(?:\w+:)?cols>[\s\S]*?<\/(?:\w+:)?cols>/)?.[0]
  const cols = templateCols?.replace(/<(\/?)\w+:/g, '<$1') || '<cols><col min="1" max="1" width="12" customWidth="1"/><col min="2" max="2" width="22" customWidth="1"/><col min="3" max="4" width="18" customWidth="1"/><col min="5" max="5" width="28" customWidth="1"/><col min="6" max="7" width="18" customWidth="1"/><col min="8" max="9" width="20" customWidth="1"/><col min="10" max="10" width="14" customWidth="1"/><col min="11" max="11" width="30" customWidth="1"/></cols>'
  const rows: string[] = []
  const addRow = (rowNumber: number, values: unknown[], styles: string[]) => {
    const cells = values.map((value, index) => inlineCell(`${columnName(index + 1)}${rowNumber}`, value, styles[index])).join('')
    rows.push(`<row r="${rowNumber}">${cells}</row>`)
  }

  addRow(1, HEADERS, headerStyles)
  let rowNumber = 2
  records.forEach(record => {
    addRow(rowNumber++, [`${record.hoja}/${record.anio}`, record.padre.nombre, record.padre.anilla, record.padre.plumaje, record.madre.nombre, record.madre.anilla, record.madre.plumaje, '', '', '', ''], pairStyles)
    record.pichones.forEach(chick => addRow(rowNumber++, ['', '', '', '', '', '', '', chick.anilla, chick.plumaje, chick.sexo, chick.observaciones], chickStyles))
    rows.push(`<row r="${rowNumber}"/>`)
    rowNumber += 1
  })

  const dimension = `A1:K${Math.max(1, rowNumber - 1)}`
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><dimension ref="${dimension}"/><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="15"/>${cols}<sheetData>${rows.join('')}</sheetData><autoFilter ref="${dimension}"/><pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/></worksheet>`
}

function relationshipTarget(rels: string, id: string) {
  const relationship = [...rels.matchAll(/<Relationship\s+([^>]*?)\/>/g)].find(match => match[1].includes(`Id="${id}"`))
  const target = relationship?.[1].match(/Target="([^"]+)"/)?.[1]
  if (!target) return ''
  return target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`
}

function xmlText(value: string) {
  return value.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&')
}

async function existingSheetKeys(zip: JSZip, workbookXml: string, relsXml: string) {
  const keys = new Set<string>()
  const sharedXml = zip.file('xl/sharedStrings.xml') ? await zip.file('xl/sharedStrings.xml')!.async('string') : ''
  const shared = [...sharedXml.matchAll(/<(?:\w+:)?si\b[^>]*>([\s\S]*?)<\/(?:\w+:)?si>/g)].map(item =>
    [...item[1].matchAll(/<(?:\w+:)?t\b[^>]*>([\s\S]*?)<\/(?:\w+:)?t>/g)].map(part => xmlText(part[1])).join('')
  )
  const tags = [...workbookXml.matchAll(/<(?:\w+:)?sheet\b[^>]*\/>/g)].map(match => match[0])
  for (const tag of tags) {
    const year = Number(tag.match(/\bname="(\d{4})"/)?.[1])
    const relId = tag.match(/\br:id="([^"]+)"/)?.[1] || ''
    const path = relationshipTarget(relsXml, relId)
    if (!year || !path || !zip.file(path)) continue
    const xml = await zip.file(path)!.async('string')
    for (const match of xml.matchAll(/<(?:\w+:)?c\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?c>/g)) {
      if (!match[1].match(/\br="A\d+"/)) continue
      const type = match[1].match(/\bt="([^"]+)"/)?.[1] || ''
      const raw = match[2].match(/<(?:\w+:)?v\b[^>]*>([\s\S]*?)<\/(?:\w+:)?v>/)?.[1]
        ?? match[2].match(/<(?:\w+:)?t\b[^>]*>([\s\S]*?)<\/(?:\w+:)?t>/)?.[1]
        ?? ''
      const value = type === 's' ? shared[Number(raw)] || '' : xmlText(raw)
      const pair = value.trim().match(/^(\d+)\s*\/\s*(\d{4})$/)
      if (pair) keys.add(`${Number(pair[2])}:${Number(pair[1])}`)
    }
  }
  return keys
}
function appendRecordsToSheetXml(existingXml: string, year: number, records: SheetDetail[]) {
  const rowNumbers = [...existingXml.matchAll(/<(?:\w+:)?row\b[^>]*\br="(\d+)"/g)].map(match => Number(match[1]))
  let rowNumber = Math.max(1, ...rowNumbers) + 2
  const prefix = existingXml.match(/<(\w+:)?worksheet\b/)?.[1] || ''
  const pairStyles = styleIdsForRow(existingXml, 2)
  const chickStyles = styleIdsForRow(existingXml, 3)
  const rows: string[] = []

  const cell = (ref: string, value: unknown, style = '') => {
    const styleAttribute = style ? ` s="${style}"` : ''
    return `<${prefix}c r="${ref}" t="inlineStr"${styleAttribute}><${prefix}is><${prefix}t xml:space="preserve">${xmlEscape(value)}</${prefix}t></${prefix}is></${prefix}c>`
  }
  const addRow = (values: unknown[], styles: string[]) => {
    const cells = values.map((value, index) => cell(`${columnName(index + 1)}${rowNumber}`, value, styles[index])).join('')
    rows.push(`<${prefix}row r="${rowNumber}">${cells}</${prefix}row>`)
    rowNumber += 1
  }

  records.forEach(record => {
    addRow([`${record.hoja}/${record.anio}`, record.padre.nombre, record.padre.anilla, record.padre.plumaje, record.madre.nombre, record.madre.anilla, record.madre.plumaje, '', '', '', ''], pairStyles)
    record.pichones.forEach(chick => addRow(['', '', '', '', '', '', '', chick.anilla, chick.plumaje, chick.sexo, chick.observaciones], chickStyles))
    rows.push(`<${prefix}row r="${rowNumber}"/>`)
    rowNumber += 1
  })

  const lastRow = rowNumber - 1
  const closingSheetData = `</${prefix}sheetData>`
  let xml = existingXml.replace(closingSheetData, `${rows.join('')}${closingSheetData}`)
  xml = xml.replace(/(<(?:\w+:)?dimension\b[^>]*\bref=")[^"]+(")/, `$1A1:K${lastRow}$2`)
  xml = xml.replace(/(<(?:\w+:)?autoFilter\b[^>]*\bref=")[^"]+(")/, `$1A1:K${lastRow}$2`)
  return xml
}

export async function mergeWorkbookWithStats(baseBytes: ArrayBuffer | Uint8Array, sheets: SheetDetail[]) {
  const zip = await JSZip.loadAsync(baseBytes)
  let workbookXml = await zip.file('xl/workbook.xml')!.async('string')
  if (!workbookXml.match(/<(\\w+:)?workbook\\b/)?.[1] && !/\\bxmlns:x=/.test(workbookXml)) workbookXml = workbookXml.replace(/<x:sheet\\b/g, '<sheet')
  let relsXml = await zip.file('xl/_rels/workbook.xml.rels')!.async('string')
  let typesXml = await zip.file('[Content_Types].xml')!.async('string')

  const sheetTags = [...workbookXml.matchAll(/<(?:\w+:)?sheet\b[^>]*\/>/g)].map(match => match[0])
  const existingKeys = await existingSheetKeys(zip, workbookXml, relsXml)
  const skippedSheets: string[] = []
  let added = 0
  const sheetTag = (name: string) => sheetTags.find(tag => new RegExp(`\\bname="${name}"`).test(tag))
  const relationId = (tag?: string) => tag?.match(/\br:id="([^"]+)"/)?.[1] || ''
  const templateTag = sheetTag('2024') || sheetTag('2023') || sheetTags[0]
  const templatePath = relationshipTarget(relsXml, relationId(templateTag))
  const templateXml = templatePath && zip.file(templatePath) ? await zip.file(templatePath)!.async('string') : ''

  const sheetNumbers = Object.keys(zip.files).map(path => path.match(/^xl\/worksheets\/sheet(\d+)\.xml$/)?.[1]).filter(Boolean).map(Number)
  let nextSheetNumber = Math.max(0, ...sheetNumbers) + 1
  const sheetIds = [...workbookXml.matchAll(/\bsheetId="(\d+)"/g)].map(match => Number(match[1]))
  let nextSheetId = Math.max(0, ...sheetIds) + 1
  let nextRelId = 1

  for (const [year, allRecords] of groupSheetsByYear(sheets)) {
    const records = allRecords.filter(record => {
      const key = `${year}:${record.hoja}`
      if (existingKeys.has(key)) { skippedSheets.push(`${record.hoja}/${year}`); return false }
      existingKeys.add(key)
      return true
    })
    if (!records.length) continue
    added += records.length
    const existingTag = sheetTag(String(year))
    let path = relationshipTarget(relsXml, relationId(existingTag))
    if (path && zip.file(path)) {
      const existingXml = await zip.file(path)!.async('string')
      zip.file(path, appendRecordsToSheetXml(existingXml, year, records))
      continue
    }

    const filename = `sheet${nextSheetNumber++}.xml`
    path = `xl/worksheets/${filename}`
    let relId = `Rpalomar${nextRelId++}`
    while (relsXml.includes(`Id="${relId}"`)) relId = `Rpalomar${nextRelId++}`
    const prefix = workbookXml.match(/<(\w+:)?workbook\b/)?.[1] || ''
    const sheetElement = `<${prefix}sheet name="${year}" sheetId="${nextSheetId++}" r:id="${relId}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" />`
    workbookXml = workbookXml.replace(/<\/(?:\w+:)?sheets>/, `${sheetElement}$&`)
    relsXml = relsXml.replace('</Relationships>', `<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="/xl/worksheets/${filename}" /></Relationships>`)
    typesXml = typesXml.replace('</Types>', `<Override PartName="/xl/worksheets/${filename}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`)
    zip.file(path, buildSheetXml(year, records, templateXml))
  }

  zip.file('xl/workbook.xml', workbookXml)
  zip.file('xl/_rels/workbook.xml.rels', relsXml)
  zip.file('[Content_Types].xml', typesXml)
  const bytes = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' })
  return { bytes, added, skipped: skippedSheets.length, skippedSheets }
}

export async function mergeWorkbookBytes(baseBytes: ArrayBuffer | Uint8Array, sheets: SheetDetail[]) {
  return (await mergeWorkbookWithStats(baseBytes, sheets)).bytes
}

export async function createNewWorkbookBytes(sheets: SheetDetail[]) {
  const workbook = new ExcelJS.Workbook()
  for (const [year, records] of groupSheetsByYear(sheets)) addYearWorksheet(workbook, year, records)
  workbook.creator = 'Palomar'
  workbook.subject = 'Hojas de cría de palomos'
  const contents = await workbook.xlsx.writeBuffer()
  return new Uint8Array(contents as ArrayBuffer)
}

export async function exportPalomarExcel(sheets: SheetDetail[], baseFile?: File) {
  if (!sheets.length && !baseFile) throw new Error('Todavía no hay hojas guardadas para exportar.')
  const merged = baseFile ? await mergeWorkbookWithStats(await baseFile.arrayBuffer(), sheets) : { bytes: await createNewWorkbookBytes(sheets), added: sheets.length, skipped: 0, skippedSheets: [] as string[] }
  const contents = merged.bytes
  const payload = contents.buffer.slice(contents.byteOffset, contents.byteOffset + contents.byteLength) as ArrayBuffer
  const blob = new Blob([payload], { type: MIME })
  const url = URL.createObjectURL(blob)
  const filename = `palomar-completo-${new Date().toISOString().slice(0, 10)}.xlsx`
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { filename, added: merged.added, skipped: merged.skipped, skippedSheets: merged.skippedSheets }
}
